import { uiSurface } from "shared/ui/uiSurface"
import { ExpandMore, Storefront } from "@mui/icons-material"
import {
  Box,
  Collapse,
  IconButton,
  Paper,
  Stack,
  Typography,
} from "@mui/material"
import { FC, memo, useState } from "react"
import { useTranslation } from "react-i18next"
import { Hauler } from "src/db/Hauler"
import { getProviderColor } from "src/features/shared-module/functions"
import { ProviderMarker } from "../map-icons"
import { useAppSelector } from "../hooks"
import { haulerDetailMapSelector } from "../slices/dashboard.slice"

export const ProviderMapLegend: FC = memo(() => {
  const { t } = useTranslation()
  const [expanded, setExpanded] = useState(false)
  const haulerDetailMap = useAppSelector(haulerDetailMapSelector)

  const providers = Object.entries(haulerDetailMap).filter(
    ([, hauler]) => hauler.address?.position,
  )

  if (providers.length === 0) return null

  return (
    <Box
      {...uiSurface("UI-DSP-014")}
      sx={{
        position: "absolute",
        bottom: 16,
        left: 16,
        zIndex: 1,
      }}
    >
      <Paper
        elevation={3}
        sx={(theme) => ({
          borderRadius: 2,
          overflow: "hidden",
          minWidth: expanded ? 220 : "auto",
          backgroundColor: theme.palette.background.paper,
        })}
      >
        <Stack
          direction="row"
          alignItems="center"
          spacing={1}
          onClick={() => setExpanded(!expanded)}
          sx={(theme) => ({
            px: 1.5,
            py: 0.75,
            cursor: "pointer",
            "&:hover": {
              backgroundColor: theme.palette.action.hover,
            },
          })}
        >
          <Storefront sx={{ fontSize: 18, color: "text.secondary" }} />
          <Typography variant="subtitle2" sx={{ fontWeight: 600, flex: 1 }}>
            {t("providers")}
          </Typography>
          <IconButton size="small" sx={{ p: 0 }}>
            <ExpandMore
              sx={{
                fontSize: 18,
                transform: expanded ? "rotate(180deg)" : "rotate(0deg)",
                transition: "transform 0.2s",
              }}
            />
          </IconButton>
        </Stack>
        <Collapse in={expanded}>
          <Stack spacing={0} sx={{ pb: 0.5 }}>
            {providers.map(([, hauler], index) => (
              <ProviderLegendItem
                key={hauler.id}
                hauler={hauler}
                color={getProviderColor(index)}
              />
            ))}
          </Stack>
        </Collapse>
      </Paper>
    </Box>
  )
})

const ProviderLegendItem: FC<{ hauler: Hauler; color: string }> = ({
  hauler,
  color,
}) => (
  <Stack
    direction="row"
    alignItems="center"
    spacing={1}
    sx={(theme) => ({
      px: 1.5,
      py: 0.5,
      minHeight: 32,
      "&:hover": {
        backgroundColor: theme.palette.action.hover,
      },
    })}
  >
    <Box
      sx={{
        width: 18,
        height: 18,
        flexShrink: 0,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <ProviderMarker color={color} />
    </Box>
    <Typography
      variant="caption"
      noWrap
      sx={{ maxWidth: 170, fontWeight: 500, lineHeight: 1.2 }}
    >
      {hauler.name}
    </Typography>
  </Stack>
)
